import { QuestionType } from "@fhc/shared/constants";
import { useCallback, useState } from "react";

/**
 * @typedef {object} SurveyNavigation
 * @property {{ [key: string]: number }} tableQuestionIndices Object that holds indices for table questions. The table questions
 * need their own navigation logic to support moving forward/back through table pages without going to the next question.
 * @property {(tableQuestionIndices: { [key: string]: number }) => void} setTableQuestionIndices Function for updating the `tableQuestionIndices` state directly.
 * @property {(questions: object[]) => void} initializeTableQuestionIndices Helper for setting the `tableQuestionIndices` state. It creates the `tableQuestionIndices` object from an array of questions.
 * @property {(questionId: number, steps: number) => void} navigateTable Function for table question navigation. Use positive values to go forward and negative values to go backwards.
 * @property {number} questionIndex The index of the current question.
 * @property {(steps: number) => void} navigateQuestion Function for survey question navigation. Use positive values to go forward and negative values to go backwards.
 */

/**
 * Custom React hook for survey navigation.
 * @returns {SurveyNavigation} An array containing the current page and a function to navigate between pages.
 */
function useSurveyNavigation() {
    const [tableQuestionIndices, setTableQuestionIndices] = useState(null);
    const [questionIndex, setQuestionIndex] = useState(0);

    const initializeTableQuestionIndices = useCallback((questions) => {
        const initialTableQuestionIndices = {};

        questions
            .filter((question) => question.type === QuestionType.MATRIX)
            .forEach((question) => {
                initialTableQuestionIndices[question.id] = 0;
            });

        setTableQuestionIndices(initialTableQuestionIndices);
    }, []);

    return {
        tableQuestionIndices,
        questionIndex,
        setTableQuestionIndices,
        initializeTableQuestionIndices,
        navigateTable: (questionId, steps) =>
            setTableQuestionIndices((previousIndices) => ({
                ...previousIndices,
                [questionId]: previousIndices[questionId] + steps,
            })),
        navigateQuestion: (steps) => setQuestionIndex((previousIndex) => previousIndex + steps),
    };
}

export default useSurveyNavigation;
